'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { tryRefreshToken } from '@/shared/libs/authSession';
import logger from '@/shared/libs/logger.client';
import {
  type IdlePhase,
  idlePhase,
  type IdleTimeoutSetting,
  rotateEveryMs,
  shouldRotate,
} from '@/shared/libs/sessionIdle';

/**
 * Ce qui compte comme un geste.
 *
 * `pointerdown` et non `pointermove` : un curseur qui traverse l'écran parce qu'on va chercher un
 * café n'est pas de l'activité, et entretiendrait une session que personne n'utilise — exactement
 * ce que le réglage demande de couper.
 */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

/** Le pouls du décompte. Une seconde : c'est la précision qu'affiche l'avertissement. */
const TICK_MS = 1_000;

type SessionIdleGuard = {
  phase: IdlePhase;
  /** Renouvelle tout de suite — le bouton « Rester connecté ». */
  keepAlive: () => Promise<boolean>;
};

/**
 * Entretient la session tant que l'utilisateur agit, et dit où elle en est.
 *
 * Voir `sessionIdle.ts` pour le raisonnement : le serveur mesure le temps écoulé depuis le dernier
 * renouvellement, pas depuis le dernier geste, et le web ne renouvelait qu'en réaction à un 401.
 *
 * @param setting réglage de l'organisation — `undefined` tant que l'API ne le sert pas.
 * @param enabled session ouverte, hors aperçu démo.
 */
export function useSessionIdleGuard(
  setting: IdleTimeoutSetting,
  enabled: boolean,
): SessionIdleGuard {
  const [phase, setPhase] = useState<IdlePhase>({ kind: 'safe' });

  /** Dernier geste. Le chargement de la page en est un : on y est arrivé volontairement. */
  const lastActivityAt = useRef<number>(Date.now());
  /** Dernier renouvellement fait par cet onglet — l'ancre de l'échéance d'inactivité. */
  const rotatedAt = useRef<number | null>(null);
  /** Fin absolue de session, servie par le renouvellement. `undefined` tant qu'on ne l'a pas vue. */
  const sessionExpiresAt = useRef<string | null | undefined>(undefined);
  /** Un renouvellement en vol ne doit pas en déclencher un second. */
  const rotating = useRef(false);
  /**
   * Dernière **tentative**, réussie ou non — distincte de `rotatedAt`, qui ne retient que les
   * réussites.
   *
   * C'est elle qui espace les essais. Sans cette distinction, un échec laissait `rotatedAt` à
   * `null`, donc la condition « il manque une première ancre » vraie, donc une nouvelle tentative
   * **à chaque battement** : une requête par seconde, indéfiniment, tant que l'échec se répète.
   *
   * Et l'échec peut se répéter sans fermer la session : `tryRefreshToken` ne la vide que sur un
   * refus du serveur. Une erreur réseau ou une réponse illisible se contentent de rendre faux — la
   * garde restait donc active, à marteler une route qui sert aussi les navigations.
   */
  const lastAttemptAt = useRef<number | null>(null);
  /**
   * L'avertissement fige l'entretien automatique.
   *
   * Sans ce verrou, bouger la souris devant la fenêtre la ferait disparaître avant d'avoir été
   * lue. L'utilisateur n'apprendrait jamais que sa session se ferme sur inactivité — et le
   * découvrirait le jour où l'onglet est resté en arrière-plan, sans avertissement possible.
   */
  const warned = useRef(false);

  const rotate = useCallback(async () => {
    if (rotating.current) return false;
    rotating.current = true;
    lastAttemptAt.current = Date.now();
    try {
      const outcome = await tryRefreshToken();
      if (outcome.ok) {
        rotatedAt.current = Date.now();
        /*
          La fin absolue n'arrive que par ici (suivora-api#927), et **le champ absent ne l'efface
          pas** : une réponse qui ne la porte pas dit « je ne sais pas », pas « il n'y en a plus ».
          `null`, lui, dit qu'aucun plafond n'est configuré, et remplace bien la valeur précédente.
        */
        if (outcome.sessionExpiresAt !== undefined) {
          sessionExpiresAt.current = outcome.sessionExpiresAt;
        }
      } else {
        logger.debug('[sessionIdle] renouvellement refusé — la session est déjà fermée');
      }
      return outcome.ok;
    } finally {
      rotating.current = false;
    }
  }, []);

  const keepAlive = useCallback(async () => {
    const ok = await rotate();
    if (ok) {
      warned.current = false;
      lastActivityAt.current = Date.now();
      setPhase({ kind: 'safe' });
    }
    return ok;
  }, [rotate]);

  useEffect(() => {
    if (!enabled) return;

    const markActivity = () => {
      lastActivityAt.current = Date.now();
    };
    ACTIVITY_EVENTS.forEach(type => window.addEventListener(type, markActivity, { passive: true }));
    return () => {
      ACTIVITY_EVENTS.forEach(type => window.removeEventListener(type, markActivity));
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      rotatedAt.current = null;
      lastAttemptAt.current = null;
      sessionExpiresAt.current = undefined;
      warned.current = false;
      setPhase({ kind: 'safe' });
      return;
    }

    const interval = rotateEveryMs(setting);

    const tick = () => {
      const now = Date.now();
      const next = idlePhase({
        setting,
        rotatedAt: rotatedAt.current,
        sessionExpiresAt: sessionExpiresAt.current,
        now,
      });
      if (next.kind === 'warn') warned.current = true;
      /*
        Le rendu ne se réveille qu'en phase d'avertissement, où le décompte change chaque seconde.
        Hors de là, publier un objet neuf toutes les secondes ferait re-rendre l'application entière
        pour dire la même chose.
      */
      setPhase(prev => (prev.kind === next.kind && next.kind !== 'warn' ? prev : next));

      if (warned.current || next.kind === 'expired') return;

      /*
        Onglet visible seulement. Un onglet d'arrière-plan ne reçoit aucun geste, donc ne
        renouvellerait de toute façon pas — mais la condition évite surtout que plusieurs onglets
        se renouvellent au même instant : la rotation révoque le jeton précédent, et deux appels
        simultanés se coupent l'un l'autre.
      */
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;

      // La règle vit dans `sessionIdle.ts`, où elle est vérifiée : c'est elle qui décide du
      // nombre de requêtes envoyées à une route qui sert aussi les navigations (#1241).
      const due = shouldRotate({
        lastAttemptAt: lastAttemptAt.current,
        rotatedAt: rotatedAt.current,
        lastActivityAt: lastActivityAt.current,
        interval,
        now,
      });
      if (due) void rotate();
    };

    tick();
    const timer = window.setInterval(tick, TICK_MS);
    return () => window.clearInterval(timer);
  }, [enabled, setting, rotate]);

  return { phase, keepAlive };
}
